import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {ControlMessagesComponent} from '@brunata-angular/ui';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {EventTableComponent} from './events/events.component';
import {catchError, combineLatest, map, of, switchMap} from 'rxjs';
import {MeterService} from '../meter.service';
import {
    AppValidators,
    filterNulls,
    fromControl,
    keyComparator,
    TranslatePipe,
} from '@brunata-angular/util';
import {selectBuilding} from 'src/app/state/selectors';
import {Store} from '@ngrx/store';

export type MetaData = {
    meterNo: string;
    serialNo: string;
    meterPlacement: string;
    meterId: string;
};

type DateForm = {
    startDate: FormControl<DateTime | null>;
    endDate: FormControl<DateTime | null>;
};

@Component({
    selector: 'app-event-alarms',
    templateUrl: './event-alarms.component.html',
    styleUrl: './event-alarms.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        TranslatePipe,
        ReactiveFormsModule,
        InputDateGroupComponent,
        ControlMessagesComponent,
        EventTableComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventAlarmsComponent {
    private readonly store = inject(Store);

    formGroup: FormGroup<DateForm> = this.formBuilder.group(
        {
            startDate: new FormControl<DateTime>(
                DateTime.now().minus({months: 1}),
                Validators.required,
            ),
            endDate: new FormControl<DateTime>(DateTime.now(), Validators.required),
        },
        {validators: AppValidators.dateIsFirst},
    );

    readonly view$ = combineLatest([
        this.route.paramMap,
        fromControl(this.formGroup),
        this.store.select(selectBuilding).pipe(
            map(buildingState =>
                buildingState.status === 'success' ? buildingState.buildingNo : null,
            ),
            filterNulls(),
        ),
    ]).pipe(
        switchMap(([params, formControl, buildingNo]) => {
            if (formControl.startDate != null && formControl.endDate != null) {
                return this.meterService
                    .getEventAlarms(
                        buildingNo,
                        formControl.startDate,
                        formControl.endDate,
                        params.get('meterid') ?? '',
                    )
                    .pipe(
                        map(item => {
                            const data = item.eventTypes.sort(
                                keyComparator(document => document.created, 'desc'),
                            );

                            return {
                                metaData: {
                                    meterNo: item.meterNo ?? '',
                                    serialNo: item.printedSerialNo ?? '',
                                    meterPlacement: item.meterPlacement ?? '',
                                    meterId: params.get('meterId') ?? '',
                                },
                                critical: data.filter(event => event.severity === 'CRITICAL'),
                                warning: data.filter(event => event.severity === 'WARNING'),
                                info: data.filter(event => event.severity === 'INFO'),
                            };
                        }),
                        catchError(() =>
                            of({
                                metaData: {
                                    meterNo: '',
                                    serialNo: '',
                                    meterPlacement: '',
                                    meterId: params.get('meterId') ?? '',
                                },
                                critical: [],
                                warning: [],
                                info: [],
                            }),
                        ),
                    );
            } else {
                return of({
                    metaData: {
                        meterNo: '',
                        serialNo: '',
                        meterPlacement: '',
                        meterId: params.get('meterId') ?? '',
                    },
                    critical: [],
                    warning: [],
                    info: [],
                });
            }
        }),
    );

    constructor(
        private formBuilder: FormBuilder,
        private meterService: MeterService,
        private route: ActivatedRoute,
    ) {}
}
